import { useEffect, useState } from "react";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { baseInfoQueryOptions } from "../api/base-info";
import { changeUiLocale, supportedLocales, type UiLocale } from "../locales/config";
import { PageError } from "./page-error";
import { queryClient } from "./query-client";
import { router } from "./router";

function AppBootstrap() {
  const { t, i18n } = useTranslation();
  // 安装握手已并入 base_info，直接取即可。
  const info = useQuery(baseInfoQueryOptions());
  // 服务端存的是持久化的那一份，localStorage 只是启动时的快速值，两边不一致时以服务端为准。
  const saved = info.data?.language;
  const savedLocale =
    saved && supportedLocales.includes(saved as UiLocale) ? (saved as UiLocale) : undefined;
  // 只在启动时应用一次：baseInfo 的 staleTime 是 Infinity，保存语言后它不会变，
  // 若一直拿它对齐，用户切换语言会立刻被这份过期缓存切回去。
  const [applied, setApplied] = useState(false);
  const localePending = savedLocale !== undefined && !applied;

  useEffect(() => {
    if (savedLocale === undefined || applied) return;
    setApplied(true);
    if (i18n.language !== savedLocale) void changeUiLocale(savedLocale);
  }, [savedLocale, applied, i18n.language]);

  if (info.isPending || localePending) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
      >
        <s-spinner accessibilityLabel={t("LoadingBlyCare")} size="large" />
        <p className="text-sm text-gray-600">{t("LoadingBlyCare")}</p>
      </div>
    );
  }

  if (info.error) {
    return (
      <PageError
        error={info.error}
        onRetry={() => {
          void info.refetch();
        }}
      />
    );
  }
  return <RouterProvider router={router} />;
}

export function AppProviders() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppBootstrap />
    </QueryClientProvider>
  );
}
